import type { CreateEmployerInput } from '@duke-hq/svc-api/admin/trpc/routers/workPlacements/employers/schemas';
import {
  hasNamedContact,
  type BusinessDetails,
  type EmployerAddress,
  type PlacementsDetails,
} from './types';

function toNullable(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

// Composes the createEmployer payload from the wizard's collected slices. The
// address component emits `addressStreetSubpremise`; the employer API stores
// `addressSubpremise`, so that one field is mapped here. Compliance documents
// are only sent when the "ready for placements" toggle is on; capacity and
// exclusion are not part of this flow.
export function buildPayload(
  business: BusinessDetails,
  address: EmployerAddress | null,
  placements: PlacementsDetails,
  courseIds: Set<string>,
): CreateEmployerInput {
  const ready = placements.readyForPlacements;
  return {
    name: business.name.trim(),
    abn: toNullable(business.abn),
    businessType: toNullable(business.businessType),
    formattedAddress: address?.formattedAddress ?? null,
    addressSubpremise: address?.addressStreetSubpremise ?? null,
    addressStreetNumber: address?.addressStreetNumber ?? null,
    addressStreetName: address?.addressStreetName ?? null,
    addressLocality: address?.addressLocality ?? null,
    addressState: address?.addressState ?? null,
    addressPostcode: address?.addressPostcode ?? null,
    contact: hasNamedContact(business.contact)
      ? {
          firstName: business.contact.firstName.trim(),
          lastName: business.contact.lastName.trim(),
          email: toNullable(business.contact.email),
          phone: toNullable(business.contact.phone),
          preferredContactMethod:
            business.contact.preferredContactMethod === ''
              ? null
              : business.contact.preferredContactMethod,
        }
      : null,
    placementCapacity: null,
    agreementFileDrns: ready && placements.agreement != null ? [placements.agreement.drn] : [],
    agreementExpiresAt:
      ready && placements.agreementExpiresAt !== ''
        ? new Date(placements.agreementExpiresAt)
        : null,
    insuranceFileDrns: ready && placements.insurance != null ? [placements.insurance.drn] : [],
    insuranceExpiresAt:
      ready && placements.insuranceExpiresAt !== ''
        ? new Date(placements.insuranceExpiresAt)
        : null,
    courseIds: [...courseIds],
  };
}
